// src/services/recruiter.js
import { db } from './firebase';
import { 
    collection, 
    getDocs, 
    getDoc, 
    doc, 
    setDoc, 
    addDoc, 
    updateDoc, 
    deleteDoc,
    query, 
    where, 
    orderBy 
} from "firebase/firestore";
import { auth } from './firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";

// ============================================
// RECRUITER SIGN UP
// ============================================
export const recruiterSignUp = async (email, password, userData) => {
    try {
        // Check if user already exists
        try {
            await signInWithEmailAndPassword(auth, email, password);
            return { success: false, error: "Email already registered. Please login instead." };
        } catch (signInError) {
            if (signInError.code !== 'auth/user-not-found') {
                console.error("Sign in check error:", signInError);
            }
        }

        // Create auth user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Save recruiter data
        const recruiterData = {
            ...userData,
            email: email,
            uid: user.uid,
            status: 'pending',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            role: 'recruiter',
            tokens: 0,
            unlockedCandidates: [] // ADDED: Track unlocked candidates
        };

        // Save to recruiters collection
        await setDoc(doc(db, 'recruiters', user.uid), recruiterData);

        // Save to users collection
        await setDoc(doc(db, 'users', user.uid), {
            email: email,
            role: 'recruiter',
            status: 'pending',
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            createdAt: new Date().toISOString(),
            uid: user.uid
        }, { merge: true });

        return { success: true, user, status: 'pending' };
    } catch (error) {
        console.error("Recruiter sign up error:", error);
        if (error.code === 'auth/email-already-in-use') {
            return { success: false, error: "Email already registered. Please login instead." };
        }
        if (error.code === 'auth/weak-password') {
            return { success: false, error: "Password should be at least 6 characters." };
        }
        if (error.code === 'auth/too-many-requests') {
            return { success: false, error: "Too many requests. Please try again later." };
        }
        if (error.code === 'auth/network-request-failed') {
            return { success: false, error: "Network error. Please check your internet connection." };
        }
        return { success: false, error: "Sign up failed. Please try again." };
    }
};

// ============================================
// RECRUITER STATUS
// ============================================
export const getRecruiterStatus = async (uid) => {
    try {
        const docRef = doc(db, 'recruiters', uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data().status || 'pending';
        }
        return null;
    } catch (error) {
        console.error("Error getting recruiter status:", error);
        return null;
    }
};

// ============================================
// RECRUITER PROFILE
// ============================================
export const getRecruiterProfile = async (uid) => {
    try {
        const docRef = doc(db, 'recruiters', uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        }
        return null;
    } catch (error) {
        console.error("Error getting recruiter profile:", error);
        return null;
    }
};

// ============================================
// GET RECRUITER BY EMAIL
// ============================================
export const getRecruiterByEmail = async (email) => {
    try {
        const q = query(collection(db, 'recruiters'), where('email', '==', email));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            return querySnapshot.docs[0].data();
        }
        return null;
    } catch (error) {
        console.error("Error getting recruiter by email:", error);
        return null;
    }
};

// ============================================
// GET ALL RECRUITERS (ADMIN)
// ============================================
export const getAllRecruiters = async () => {
    try {
        const q = query(collection(db, 'recruiters'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const recruiters = [];
        querySnapshot.forEach((doc) => {
            recruiters.push({ id: doc.id, ...doc.data() });
        });
        return recruiters;
    } catch (error) {
        console.error("Error getting recruiters:", error);
        return [];
    }
};

// ============================================
// UPDATE RECRUITER STATUS (ADMIN)
// ============================================
export const updateRecruiterStatus = async (uid, status) => {
    try {
        // Check if recruiter document exists
        const recruiterRef = doc(db, 'recruiters', uid);
        const recruiterSnap = await getDoc(recruiterRef);
        
        if (!recruiterSnap.exists()) {
            // Create recruiter document if it doesn't exist
            console.log("Recruiter document not found, creating...");
            
            // Try to get user data from users collection
            const userRef = doc(db, 'users', uid);
            const userSnap = await getDoc(userRef);
            const userData = userSnap.exists() ? userSnap.data() : {};
            
            // Create recruiter document with default data
            await setDoc(recruiterRef, {
                uid: uid,
                email: userData.email || '',
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                status: status,
                role: 'recruiter',
                tokens: 0,
                unlockedCandidates: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                businessType: 'company',
                contactNumber: userData.phoneNumber || '',
                companyName: userData.companyName || '',
            }, { merge: true });
        } else {
            // Update existing recruiter document
            await updateDoc(recruiterRef, {
                status: status,
                updatedAt: new Date().toISOString()
            });
        }
        
        // Also update users collection
        await updateDoc(doc(db, 'users', uid), {
            status: status
        });
        
        return { success: true };
    } catch (error) {
        console.error("Error updating recruiter status:", error);
        return { success: false, error: error.message };
    }
};

// ============================================
// ASSIGN TOKENS (ADMIN)
// ============================================
export const assignTokens = async (uid, amount) => {
    try {
        const docRef = doc(db, 'recruiters', uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
            const currentTokens = docSnap.data().tokens || 0;
            await updateDoc(docRef, {
                tokens: currentTokens + amount,
                updatedAt: new Date().toISOString()
            });
        } else {
            // Create recruiter document if it doesn't exist
            await setDoc(docRef, {
                uid: uid,
                tokens: amount,
                unlockedCandidates: [],
                status: 'pending',
                role: 'recruiter',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            }, { merge: true });
        }
        
        // Also log token transaction
        await addDoc(collection(db, 'tokenLogs'), {
            recruiterId: uid,
            amount: amount,
            type: 'admin_grant',
            description: `Admin added ${amount} tokens`,
            createdAt: new Date().toISOString()
        });
        
        return { success: true };
    } catch (error) {
        console.error("Error assigning tokens:", error);
        return { success: false, error: error.message };
    }
};

// ============================================
// GET RECRUITER TOKENS
// ============================================
export const getRecruiterTokens = async (uid) => {
    try {
        const docRef = doc(db, 'recruiters', uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data().tokens || 0;
        }
        return 0;
    } catch (error) {
        console.error("Error getting recruiter tokens:", error);
        return 0;
    }
};

// ============================================
// DEDUCT TOKENS (When recruiter unlocks candidate)
// ============================================
export const deductTokens = async (uid, amount, description = '') => {
    try {
        const docRef = doc(db, 'recruiters', uid);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
            return { success: false, error: "Recruiter not found" };
        }
        
        const currentTokens = docSnap.data().tokens || 0;
        
        if (currentTokens < amount) {
            return { success: false, error: "Insufficient tokens" };
        }
        
        await updateDoc(docRef, {
            tokens: currentTokens - amount,
            updatedAt: new Date().toISOString()
        });
        
        // Log token transaction
        await addDoc(collection(db, 'tokenLogs'), {
            recruiterId: uid,
            amount: -amount,
            type: 'spend',
            description: description || `Spent ${amount} tokens`,
            createdAt: new Date().toISOString()
        });
        
        return { success: true, newBalance: currentTokens - amount };
    } catch (error) {
        console.error("Error deducting tokens:", error);
        return { success: false, error: error.message };
    }
};

// ============================================
// GET TOKEN TRANSACTION HISTORY
// ============================================
export const getTokenHistory = async (uid) => {
    try {
        const q = query(
            collection(db, 'tokenLogs'),
            where('recruiterId', '==', uid),
            orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const logs = [];
        querySnapshot.forEach((doc) => {
            logs.push({ id: doc.id, ...doc.data() });
        });
        return logs;
    } catch (error) {
        console.error("Error getting token history:", error);
        return [];
    }
};

// ============================================
// UNLOCKED CANDIDATES MANAGEMENT (NEW)
// ============================================

// Save unlocked candidate to Firebase
export const saveUnlockedCandidate = async (recruiterId, candidateId) => {
    try {
        const recruiterRef = doc(db, 'recruiters', recruiterId);
        const recruiterSnap = await getDoc(recruiterRef);
        
        if (!recruiterSnap.exists()) {
            return { success: false, error: "Recruiter not found" };
        }
        
        const currentUnlocked = recruiterSnap.data().unlockedCandidates || [];
        
        // Check if already unlocked
        if (currentUnlocked.includes(candidateId)) {
            return { success: true, alreadyUnlocked: true };
        }
        
        // Add to unlocked candidates array
        await updateDoc(recruiterRef, {
            unlockedCandidates: [...currentUnlocked, candidateId],
            updatedAt: new Date().toISOString()
        });
        
        return { success: true };
    } catch (error) {
        console.error("Error saving unlocked candidate:", error);
        return { success: false, error: error.message };
    }
};

// Get unlocked candidates from Firebase
export const getUnlockedCandidates = async (recruiterId) => {
    try {
        const recruiterRef = doc(db, 'recruiters', recruiterId);
        const recruiterSnap = await getDoc(recruiterRef);
        
        if (!recruiterSnap.exists()) {
            return [];
        }
        
        return recruiterSnap.data().unlockedCandidates || [];
    } catch (error) {
        console.error("Error getting unlocked candidates:", error);
        return [];
    }
};

// Check if candidate is unlocked
export const isCandidateUnlocked = async (recruiterId, candidateId) => {
    try {
        const unlocked = await getUnlockedCandidates(recruiterId);
        return unlocked.includes(candidateId);
    } catch (error) {
        console.error("Error checking unlocked candidate:", error);
        return false;
    }
};

// ============================================
// RECRUITER DASHBOARD STATS
// ============================================
export const getRecruiterStats = async (uid) => {
    try {
        // Get recruiter profile
        const profile = await getRecruiterProfile(uid);
        
        // Get jobs posted by recruiter
        const jobsRef = collection(db, 'jobs');
        const q = query(jobsRef, where('postedBy', '==', uid));
        const jobsSnapshot = await getDocs(q);
        const jobs = [];
        jobsSnapshot.forEach((doc) => {
            jobs.push({ id: doc.id, ...doc.data() });
        });
        
        // Calculate total applications
        let totalApplications = 0;
        jobs.forEach(job => {
            totalApplications += job.applications?.length || 0;
        });
        
        return {
            tokens: profile?.tokens || 0,
            totalJobs: jobs.length,
            totalApplications: totalApplications,
            activeJobs: jobs.filter(j => new Date(j.closingDate) > new Date()).length,
            jobs: jobs
        };
    } catch (error) {
        console.error("Error getting recruiter stats:", error);
        return {
            tokens: 0,
            totalJobs: 0,
            totalApplications: 0,
            activeJobs: 0,
            jobs: []
        };
    }
};

// ============================================
// DELETE RECRUITER (ADMIN)
// ============================================
export const deleteRecruiter = async (uid) => {
    try {
        // Delete from recruiters collection
        await deleteDoc(doc(db, 'recruiters', uid));
        
        // Delete from users collection
        await deleteDoc(doc(db, 'users', uid));
        
        // Delete token logs (optional - keep for audit)
        // Or just delete the recruiter reference
        
        return { success: true };
    } catch (error) {
        console.error("Error deleting recruiter:", error);
        return { success: false, error: error.message };
    }
};